/**
 * Shared asset depreciation logic.
 *
 * Used by:
 *  - ToolsEquipmentView (total-operations) — operational asset register
 *  - AssetValuationView (total-finance) — finance-facing valuation view
 *
 * Both modules read the same `asset` work items from the API, so the
 * depreciation calculation must be identical to avoid discrepancies
 * between operational and financial reporting.
 */

export type DepreciationMethod =
  | 'straight-line'
  | 'declining-balance'
  | 'units_of_production'
  | 'sum_of_years_digits'
  | 'none';

export interface AssetCustomFields {
  serial_number?: string;
  barcode?: string;
  equipment_status?: string;
  category?: string;
  /** Person responsible for the asset (custodian). */
  custodian?: string;
  /** Custodian user ID (if linked to a system user). */
  custodian_user_id?: number;
  purchase_price?: number;
  purchase_date?: string;
  supplier?: string;
  warranty_expiry_date?: string;
  depreciation_method?: DepreciationMethod;
  useful_life_months?: number;
  salvage_value?: number;
  /** Currency code for purchase price / salvage value (e.g. 'ZAR', 'USD'). Defaults to 'ZAR'. */
  currency?: string;
  /** Units produced in the current period (for units_of_production method). */
  units_produced?: number;
  /** Total estimated units over the asset's useful life (for units_of_production method). */
  total_estimated_units?: number;
  // --- Tax depreciation (for tax vs book comparison) ---
  /** Tax depreciation method (may differ from book method). */
  tax_depreciation_method?: DepreciationMethod;
  /** Tax useful life in months (may differ from book). */
  tax_useful_life_months?: number;
  /** Tax salvage value (may differ from book). */
  tax_salvage_value?: number;
  // Maintenance cost tracking (from AssetMaintenanceManagementView)
  total_maintenance_cost?: string | number | null;
  purchase_cost?: string | number | null;
  annual_maintenance_budget?: string | number | null;
  // Operational fields (ignored by finance view but present in data)
  checked_out_at?: string;
  checked_out_to?: number;
  checked_out_by_name?: string;
  checked_in_at?: string;
  checked_in_by_name?: string;
  expected_return_date?: string;
  check_out_notes?: string;
  checkout_condition?: string;
  checkin_condition?: string;
  checkout_photos?: Record<string, string>;
  checkin_photos?: Record<string, string>;
  damage_reported?: boolean;
  damage_reported_at?: string;
  damage_reported_by?: number;
  damage_reported_by_name?: string;
  inspection_frequency?: string;
  last_inspection_date?: string;
  last_inspection_by?: number;
  last_inspection_by_name?: string;
  last_inspection_result?: 'pass' | 'fail' | 'needs-attention';
  last_inspection_notes?: string;
  next_inspection_date?: string;
  inspection_history?: Array<{
    date: string;
    by: number;
    by_name: string;
    result: 'pass' | 'fail' | 'needs-attention';
    notes?: string;
    condition?: string;
  }>;
  location?: string;
  model?: string;
  manufacturer?: string;
}

export interface DepreciationResult {
  currentValue: number;
  totalDepreciation: number;
  monthlyDepreciation: number;
  percentDepreciated: number;
  remainingMonths: number;
}

/**
 * Calculate depreciation for an asset based on its custom fields.
 *
 * Supports:
 *  - straight-line: (purchase_price - salvage) / useful_life_months per month
 *  - declining-balance: double-declining balance (2 / useful_life_months rate)
 *  - units_of_production: (cost - salvage) × (units_produced / total_estimated_units)
 *  - sum_of_years_digits: (cost - salvage) × (remaining_life / SYD) per year
 *
 * Returns null if required fields are missing or method is 'none'.
 */
export function calculateDepreciation(cf: AssetCustomFields): DepreciationResult | null {
  const { purchase_price, purchase_date, depreciation_method, useful_life_months, salvage_value } = cf;
  if (!purchase_price || !purchase_date || !useful_life_months || depreciation_method === 'none') return null;

  const purchaseDate = new Date(purchase_date);
  const now = new Date();
  const monthsElapsed = Math.max(
    0,
    (now.getFullYear() - purchaseDate.getFullYear()) * 12 + (now.getMonth() - purchaseDate.getMonth()),
  );
  const depreciableAmount = purchase_price - (salvage_value || 0);
  const remainingMonths = Math.max(0, useful_life_months - monthsElapsed);

  if (depreciation_method === 'straight-line') {
    const monthlyDep = depreciableAmount / useful_life_months;
    const totalDep = Math.min(depreciableAmount, monthlyDep * monthsElapsed);
    const currentVal = Math.max(salvage_value || 0, purchase_price - totalDep);
    return {
      currentValue: Math.round(currentVal * 100) / 100,
      totalDepreciation: Math.round(totalDep * 100) / 100,
      monthlyDepreciation: Math.round(monthlyDep * 100) / 100,
      percentDepreciated: depreciableAmount > 0 ? Math.min(100, Math.round((totalDep / depreciableAmount) * 100)) : 0,
      remainingMonths,
    };
  }

  if (depreciation_method === 'declining-balance') {
    const rate = 2 / useful_life_months;
    let currentVal = purchase_price;
    for (let i = 0; i < monthsElapsed && currentVal > (salvage_value || 0); i++) {
      currentVal -= currentVal * rate;
      if (currentVal < (salvage_value || 0)) currentVal = salvage_value || 0;
    }
    const totalDep = purchase_price - currentVal;
    return {
      currentValue: Math.round(currentVal * 100) / 100,
      totalDepreciation: Math.round(totalDep * 100) / 100,
      monthlyDepreciation: Math.round((currentVal * rate) * 100) / 100,
      percentDepreciated: depreciableAmount > 0 ? Math.min(100, Math.round((totalDep / depreciableAmount) * 100)) : 0,
      remainingMonths,
    };
  }

  if (depreciation_method === 'units_of_production') {
    const unitsProduced = Number(cf.units_produced || 0);
    const totalEstimatedUnits = Number(cf.total_estimated_units || 0);
    if (totalEstimatedUnits <= 0) return null;
    const totalDep = Math.min(depreciableAmount, depreciableAmount * (unitsProduced / totalEstimatedUnits));
    const currentVal = Math.max(salvage_value || 0, purchase_price - totalDep);
    // Monthly depreciation is not meaningful for UoP; report per-unit rate instead
    const perUnitDep = depreciableAmount / totalEstimatedUnits;
    return {
      currentValue: Math.round(currentVal * 100) / 100,
      totalDepreciation: Math.round(totalDep * 100) / 100,
      monthlyDepreciation: Math.round(perUnitDep * 100) / 100,
      percentDepreciated: depreciableAmount > 0 ? Math.min(100, Math.round((totalDep / depreciableAmount) * 100)) : 0,
      remainingMonths,
    };
  }

  if (depreciation_method === 'sum_of_years_digits') {
    const years = Math.ceil(useful_life_months / 12);
    const yearsElapsed = Math.floor(monthsElapsed / 12);
    // Sum of years digits: N + (N-1) + ... + 1 = N*(N+1)/2
    const syd = (years * (years + 1)) / 2;
    if (syd <= 0) return null;
    // Depreciation for year k (1-indexed): (cost - salvage) * (N - k + 1) / SYD
    let totalDep = 0;
    for (let y = 1; y <= yearsElapsed && y <= years; y++) {
      totalDep += depreciableAmount * (years - y + 1) / syd;
    }
    // Add partial-year depreciation for remaining months in the current year
    const partialMonths = monthsElapsed % 12;
    if (partialMonths > 0 && yearsElapsed < years) {
      const currentYearDep = depreciableAmount * (years - yearsElapsed) / syd;
      totalDep += currentYearDep * (partialMonths / 12);
    }
    totalDep = Math.min(depreciableAmount, totalDep);
    const currentVal = Math.max(salvage_value || 0, purchase_price - totalDep);
    // Current year's monthly depreciation
    const currentYear = yearsElapsed + 1;
    const currentYearMonthlyDep = currentYear <= years
      ? (depreciableAmount * (years - yearsElapsed) / syd) / 12
      : 0;
    return {
      currentValue: Math.round(currentVal * 100) / 100,
      totalDepreciation: Math.round(totalDep * 100) / 100,
      monthlyDepreciation: Math.round(currentYearMonthlyDep * 100) / 100,
      percentDepreciated: depreciableAmount > 0 ? Math.min(100, Math.round((totalDep / depreciableAmount) * 100)) : 0,
      remainingMonths,
    };
  }

  return null;
}

/**
 * Format a number as currency. Defaults to ZAR when no currency is provided.
 */
export function formatAssetCurrency(amount: number, currency: string = 'ZAR'): string {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
}

/** Currency symbol map for input prefixes. Falls back to the code itself. */
const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  ZAR: 'R',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

/**
 * Get the symbol for a currency code (e.g. 'ZAR' → 'R').
 */
export function getAssetCurrencySymbol(currency?: string): string {
  return CURRENCY_SYMBOL_MAP[currency || 'ZAR'] || currency || 'R';
}

/**
 * Format an ISO date string as a readable date.
 */
export function formatAssetDate(date: string | undefined): string {
  if (!date) return '\u2014';
  return new Date(date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Safely convert a value that might be a string or number to a number.
 */
export function toAssetNumber(value: string | number | null | undefined): number {
  if (value == null) return 0;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(n) ? 0 : n;
}

// ============================================================================
// DEPRECIATION SCHEDULE (year-by-year projection)
// ============================================================================

export interface DepreciationScheduleYear {
  year: number;
  fiscal_year_start: string;
  fiscal_year_end: string;
  beginning_book_value: number;
  annual_depreciation: number;
  accumulated_depreciation: number;
  ending_book_value: number;
}

export interface DepreciationSchedule {
  method: DepreciationMethod;
  purchase_price: number;
  salvage_value: number;
  useful_life_months: number;
  useful_life_years: number;
  purchase_date: string;
  years: DepreciationScheduleYear[];
}

/**
 * Generate a full year-by-year depreciation schedule for an asset.
 *
 * Supports all four depreciation methods. For units_of_production, the
 * schedule assumes linear usage (total_estimated_units / useful_life_years
 * per year) since actual production varies.
 */
export function generateDepreciationSchedule(
  cf: AssetCustomFields,
  methodOverride?: DepreciationMethod,
  usefulLifeOverride?: number,
  salvageOverride?: number,
): DepreciationSchedule | null {
  const method = methodOverride || cf.depreciation_method;
  const usefulLifeMonths = usefulLifeOverride || cf.useful_life_months;
  const salvage = salvageOverride ?? cf.salvage_value ?? 0;
  const purchasePrice = cf.purchase_price || toAssetNumber(cf.purchase_cost);

  if (!method || method === 'none' || !usefulLifeMonths || !purchasePrice || !cf.purchase_date) {
    return null;
  }

  const purchaseDate = new Date(cf.purchase_date);
  const depreciableAmount = purchasePrice - salvage;
  const years = Math.ceil(usefulLifeMonths / 12);
  const scheduleYears: DepreciationScheduleYear[] = [];

  let accumulatedDepreciation = 0;
  let currentBookValue = purchasePrice;

  for (let year = 1; year <= years; year++) {
    const yearStart = new Date(purchaseDate.getFullYear() + (year - 1), purchaseDate.getMonth(), 1);
    const yearEnd = new Date(purchaseDate.getFullYear() + year, purchaseDate.getMonth(), 0);
    const beginningBookValue = currentBookValue;
    let annualDepreciation = 0;

    if (method === 'straight-line') {
      annualDepreciation = depreciableAmount / years;
    } else if (method === 'declining-balance') {
      const rate = 2 / usefulLifeMonths; // monthly rate
      const monthsInYear = year === years ? (usefulLifeMonths - (years - 1) * 12) : 12;
      let val = currentBookValue;
      for (let m = 0; m < monthsInYear && val > salvage; m++) {
        val -= val * rate;
        if (val < salvage) val = salvage;
      }
      annualDepreciation = currentBookValue - val;
    } else if (method === 'units_of_production') {
      const totalEstimatedUnits = Number(cf.total_estimated_units || 0);
      if (totalEstimatedUnits > 0) {
        // Assume even distribution for schedule projection
        const unitsPerYear = totalEstimatedUnits / years;
        annualDepreciation = (depreciableAmount / totalEstimatedUnits) * unitsPerYear;
      }
    } else if (method === 'sum_of_years_digits') {
      const syd = (years * (years + 1)) / 2;
      annualDepreciation = depreciableAmount * (years - year + 1) / syd;
    }

    // Cap at depreciable amount
    if (accumulatedDepreciation + annualDepreciation > depreciableAmount) {
      annualDepreciation = Math.max(0, depreciableAmount - accumulatedDepreciation);
    }
    // Don't depreciate below salvage
    if (currentBookValue - annualDepreciation < salvage) {
      annualDepreciation = Math.max(0, currentBookValue - salvage);
    }

    accumulatedDepreciation += annualDepreciation;
    currentBookValue -= annualDepreciation;

    scheduleYears.push({
      year,
      fiscal_year_start: yearStart.toISOString().slice(0, 10),
      fiscal_year_end: yearEnd.toISOString().slice(0, 10),
      beginning_book_value: Math.round(beginningBookValue * 100) / 100,
      annual_depreciation: Math.round(annualDepreciation * 100) / 100,
      accumulated_depreciation: Math.round(accumulatedDepreciation * 100) / 100,
      ending_book_value: Math.round(currentBookValue * 100) / 100,
    });
  }

  return {
    method,
    purchase_price: purchasePrice,
    salvage_value: salvage,
    useful_life_months: usefulLifeMonths,
    useful_life_years: years,
    purchase_date: cf.purchase_date,
    years: scheduleYears,
  };
}

export interface TaxVsBookSchedule {
  book: DepreciationSchedule | null;
  tax: DepreciationSchedule | null;
  /** Per-year comparison rows. */
  comparison: Array<{
    year: number;
    book_depreciation: number;
    tax_depreciation: number;
    difference: number;
    book_ending_value: number | null;
    tax_ending_value: number | null;
  }>;
}

/**
 * Generate a tax vs book depreciation comparison schedule.
 *
 * Uses the asset's primary depreciation settings for the book schedule and
 * the tax-specific overrides (tax_depreciation_method, tax_useful_life_months,
 * tax_salvage_value) for the tax schedule. If no tax overrides are configured,
 * the tax schedule mirrors the book schedule (difference = 0).
 */
export function generateTaxVsBookSchedule(cf: AssetCustomFields): TaxVsBookSchedule {
  const book = generateDepreciationSchedule(cf);

  const hasTaxOverride = cf.tax_depreciation_method || cf.tax_useful_life_months || cf.tax_salvage_value !== undefined;
  const tax = hasTaxOverride
    ? generateDepreciationSchedule(
        cf,
        cf.tax_depreciation_method || cf.depreciation_method,
        cf.tax_useful_life_months || cf.useful_life_months,
        cf.tax_salvage_value ?? cf.salvage_value,
      )
    : book;

  const maxYears = Math.max(
    book?.years.length || 0,
    tax?.years.length || 0,
  );

  const comparison = [];
  for (let i = 0; i < maxYears; i++) {
    const bookYear = book?.years[i];
    const taxYear = tax?.years[i];
    comparison.push({
      year: (bookYear || taxYear)!.year,
      book_depreciation: bookYear?.annual_depreciation ?? 0,
      tax_depreciation: taxYear?.annual_depreciation ?? 0,
      difference: (taxYear?.annual_depreciation ?? 0) - (bookYear?.annual_depreciation ?? 0),
      book_ending_value: bookYear?.ending_book_value ?? null,
      tax_ending_value: taxYear?.ending_book_value ?? null,
    });
  }

  return { book, tax, comparison };
}
